import { subDays, addDays } from 'date-fns';
import type { StrategyDiagnostics, StageFunnelData } from '@/types/strategy-diagnostics';

// ─── Stage Funnel helpers ────────────────────────────────────────────────────

/** Build a StageFunnelData object from a simple list of per-stage numbers */
function buildFunnel(
  totalEntered: number,
  stages: Array<{
    label: string;
    actionType: StageFunnelData['stages'][number]['actionType'];
    casesAtStage: number;
    activeAtStage: number;
    closedAtStage: number;
    promisedAtStage: number;
    escalatedAtStage: number;
    onHoldAtStage: number;
    avgDaysAtStage: number;
  }>,
): StageFunnelData {
  return {
    totalCasesEntered: totalEntered,
    stages: stages.map((s, i) => ({
      stageOrder: i + 1,
      label: s.label,
      actionType: s.actionType,
      casesAtStage: s.casesAtStage,
      activeAtStage: s.activeAtStage,
      closedAtStage: s.closedAtStage,
      promisedAtStage: s.promisedAtStage,
      escalatedAtStage: s.escalatedAtStage,
      onHoldAtStage: s.onHoldAtStage,
      percentageClosedHere: s.casesAtStage > 0 ? Math.round((s.closedAtStage / s.casesAtStage) * 100) : 0,
      avgDaysAtStage: s.avgDaysAtStage,
    })),
  };
}

export function generateMockDiagnostics(): Record<string, StrategyDiagnostics> {
  return {
    'strategy-1': {
      id: 'diag-1',
      strategyId: 'strategy-1',
      status: 'Active',
      createdBy: {
        name: 'Sarah Johnson',
        id: 'user-1',
      },
      createdAt: subDays(new Date(), 15),
      lastRun: subDays(new Date(), 2),
      nextScheduledRun: addDays(new Date(), 0.083), // ~2 hours from now
      activeAccounts: 45,
      emailsSent14Days: 89,
      expectedEmails: 90,
      successRate: 67,
      impactAnalysis: {
        successfulContacts: {
          count: 45,
          description: 'Accounts successfully contacted in the past 14 days',
        },
        totalExposure: {
          amount: 289000,
          currency: 'GBP',
          averagePerAccount: 6422,
          severity: 'medium',
        },
      },
      stageFunnel: buildFunnel(
        45,
        [
          { label: 'Day 0 – Email',       actionType: 'email',       casesAtStage: 45, activeAtStage: 33, closedAtStage: 5,  promisedAtStage: 3,  escalatedAtStage: 0, onHoldAtStage: 4,  avgDaysAtStage: 2 },
          { label: 'Day 7 – Phone Call',  actionType: 'phone_call',  casesAtStage: 37, activeAtStage: 18, closedAtStage: 8,  promisedAtStage: 4,  escalatedAtStage: 2, onHoldAtStage: 5,  avgDaysAtStage: 5 },
          { label: 'Day 14 – Escalation', actionType: 'escalation',  casesAtStage: 25, activeAtStage: 3,  closedAtStage: 6,  promisedAtStage: 4,  escalatedAtStage: 8, onHoldAtStage: 4,  avgDaysAtStage: 8 },
        ],

      ),
      auditTrail: [
        {
          id: 'audit-1',
          date: subDays(new Date(), 2),
          event: 'run_completed',
          user: {
            name: 'System',
            id: 'system',
          },
          details: 'Sent 52 emails to overdue accounts',
        },
        {
          id: 'audit-2',
          date: subDays(new Date(), 5),
          event: 'configuration_changed',
          user: {
            name: 'Sarah Johnson',
            id: 'user-1',
          },
          details: 'Changed email template',
        },
        {
          id: 'audit-3',
          date: subDays(new Date(), 10),
          event: 'run_completed',
          user: {
            name: 'System',
            id: 'system',
          },
          details: 'Sent 37 emails to overdue accounts',
        },
        {
          id: 'audit-4',
          date: subDays(new Date(), 15),
          event: 'created',
          user: {
            name: 'Sarah Johnson',
            id: 'user-1',
          },
        },
      ],
    },
    'strategy-2': {
      id: 'diag-2',
      strategyId: 'strategy-2',
      status: 'Active',
      createdBy: {
        name: 'Mike Chen',
        id: 'user-2',
      },
      createdAt: subDays(new Date(), 30),
      lastRun: subDays(new Date(), 1),
      nextScheduledRun: addDays(new Date(), 1),
      activeAccounts: 23,
      emailsSent14Days: 47,
      expectedEmails: 46,
      successRate: 85,
      impactAnalysis: {
        successfulContacts: {
          count: 23,
          description: 'High-value accounts successfully contacted',
        },
        totalExposure: {
          amount: 567000,
          currency: 'GBP',
          averagePerAccount: 24652,
          severity: 'high',
        },
      },
      stageFunnel: buildFunnel(
        23,
        [
          { label: 'Day 0 – Phone Call',     actionType: 'phone_call',     casesAtStage: 23, activeAtStage: 16, closedAtStage: 3,  promisedAtStage: 2,  escalatedAtStage: 0, onHoldAtStage: 2,  avgDaysAtStage: 1 },
          { label: 'Day 1 – Email',          actionType: 'email',          casesAtStage: 18, activeAtStage: 8,  closedAtStage: 4,  promisedAtStage: 3,  escalatedAtStage: 1, onHoldAtStage: 2,  avgDaysAtStage: 3 },
          { label: 'Day 6 – Escalation',     actionType: 'escalation',     casesAtStage: 11, activeAtStage: 1,  closedAtStage: 2,  promisedAtStage: 2,  escalatedAtStage: 5, onHoldAtStage: 1,  avgDaysAtStage: 6 },
          { label: 'Day 9 – Account Action', actionType: 'account_action', casesAtStage: 7,  activeAtStage: 0,  closedAtStage: 3,  promisedAtStage: 2,  escalatedAtStage: 1, onHoldAtStage: 1,  avgDaysAtStage: 4 },
        ],

      ),
      auditTrail: [
        {
          id: 'audit-1',
          date: subDays(new Date(), 1),
          event: 'run_completed',
          user: {
            name: 'System',
            id: 'system',
          },
          details: 'Sent 23 emails to high-value accounts',
        },
        {
          id: 'audit-2',
          date: subDays(new Date(), 7),
          event: 'configuration_changed',
          user: {
            name: 'Mike Chen',
            id: 'user-2',
          },
          details: 'Updated escalation triggers',
        },
        {
          id: 'audit-3',
          date: subDays(new Date(), 30),
          event: 'created',
          user: {
            name: 'Mike Chen',
            id: 'user-2',
          },
        },
      ],
    },
    'strategy-3': {
      id: 'diag-3',
      strategyId: 'strategy-3',
      status: 'Active',
      createdBy: {
        name: 'Sarah Johnson',
        id: 'user-1',
      },
      createdAt: subDays(new Date(), 45),
      lastRun: subDays(new Date(), 3),
      nextScheduledRun: addDays(new Date(), 2),
      activeAccounts: 12,
      emailsSent14Days: 24,
      expectedEmails: 24,
      successRate: 92,
      impactAnalysis: {
        successfulContacts: {
          count: 12,
          description: 'VIP customers contacted with personalized approach',
        },
        totalExposure: {
          amount: 156000,
          currency: 'GBP',
          averagePerAccount: 13000,
          severity: 'low',
        },
      },
      stageFunnel: buildFunnel(
        12,
        [
          { label: 'Day 0 – Email',       actionType: 'email',      casesAtStage: 12, activeAtStage: 7, closedAtStage: 2, promisedAtStage: 2, escalatedAtStage: 0, onHoldAtStage: 1, avgDaysAtStage: 3 },
          { label: 'Day 5 – Phone Call',  actionType: 'phone_call', casesAtStage: 8,  activeAtStage: 2, closedAtStage: 3, promisedAtStage: 2, escalatedAtStage: 0, onHoldAtStage: 1, avgDaysAtStage: 5 },
          { label: 'Day 15 – Escalation', actionType: 'escalation', casesAtStage: 3,  activeAtStage: 0, closedAtStage: 1, promisedAtStage: 1, escalatedAtStage: 1, onHoldAtStage: 0, avgDaysAtStage: 7 },
        ],

      ),
      auditTrail: [
        {
          id: 'audit-1',
          date: subDays(new Date(), 3),
          event: 'run_completed',
          user: {
            name: 'System',
            id: 'system',
          },
          details: 'Sent 12 personalized emails to VIP customers',
        },
        {
          id: 'audit-2',
          date: subDays(new Date(), 20),
          event: 'configuration_changed',
          user: {
            name: 'Sarah Johnson',
            id: 'user-1',
          },
          details: 'Updated VIP communication template',
        },
        {
          id: 'audit-3',
          date: subDays(new Date(), 45),
          event: 'created',
          user: {
            name: 'Sarah Johnson',
            id: 'user-1',
          },
        },
      ],
    },
    'strategy-4': {
      id: 'diag-4',
      strategyId: 'strategy-4',
      status: 'Active',
      createdBy: {
        name: 'Mike Chen',
        id: 'user-2',
      },
      createdAt: subDays(new Date(), 60),
      lastRun: subDays(new Date(), 1),
      nextScheduledRun: addDays(new Date(), 0.5),
      activeAccounts: 18,
      emailsSent14Days: 36,
      expectedEmails: 36,
      successRate: 66,
      impactAnalysis: {
        atRiskAccounts: {
          count: 8,
          description: 'Accounts showing elevated risk of non-payment',
        },
        totalExposure: {
          amount: 234000,
          currency: 'GBP',
          averagePerAccount: 13000,
          severity: 'high',
        },
      },
      stageFunnel: buildFunnel(
        18,
        [
          { label: 'Day 0 – Phone Call',     actionType: 'phone_call',     casesAtStage: 18, activeAtStage: 13, closedAtStage: 2, promisedAtStage: 1, escalatedAtStage: 0, onHoldAtStage: 2, avgDaysAtStage: 1 },
          { label: 'Day 2 – Email',          actionType: 'email',          casesAtStage: 15, activeAtStage: 8,  closedAtStage: 2, promisedAtStage: 2, escalatedAtStage: 1, onHoldAtStage: 2, avgDaysAtStage: 4 },
          { label: 'Day 9 – Account Action', actionType: 'account_action', casesAtStage: 11, activeAtStage: 5,  closedAtStage: 1, promisedAtStage: 1, escalatedAtStage: 2, onHoldAtStage: 2, avgDaysAtStage: 6 },
          { label: 'Day 14 – Escalation',    actionType: 'escalation',     casesAtStage: 9,  activeAtStage: 1,  closedAtStage: 2, promisedAtStage: 1, escalatedAtStage: 4, onHoldAtStage: 1, avgDaysAtStage: 9 },
        ],

      ),
      auditTrail: [
        {
          id: 'audit-1',
          date: subDays(new Date(), 1),
          event: 'run_completed',
          user: {
            name: 'System',
            id: 'system',
          },
          details: 'Sent 18 urgent notices to high-risk accounts',
        },
        {
          id: 'audit-2',
          date: subDays(new Date(), 14),
          event: 'configuration_changed',
          user: {
            name: 'Mike Chen',
            id: 'user-2',
          },
          details: 'Tightened risk score threshold',
        },
        {
          id: 'audit-3',
          date: subDays(new Date(), 60),
          event: 'created',
          user: {
            name: 'Mike Chen',
            id: 'user-2',
          },
        },
      ],
    },
    'strategy-5': {
      id: 'diag-5',
      strategyId: 'strategy-5',
      status: 'Active',
      createdBy: {
        name: 'Sarah Johnson',
        id: 'user-1',
      },
      createdAt: subDays(new Date(), 20),
      lastRun: subDays(new Date(), 1),
      nextScheduledRun: addDays(new Date(), 0.25),
      activeAccounts: 67,
      emailsSent14Days: 134,
      expectedEmails: 134,
      successRate: 89,
      impactAnalysis: {
        successfulContacts: {
          count: 67,
          description: 'Early intervention contacts preventing overdue accounts',
        },
        totalExposure: {
          amount: 178000,
          currency: 'GBP',
          averagePerAccount: 2657,
          severity: 'low',
        },
      },
      stageFunnel: buildFunnel(
        67,
        [
          { label: 'Day 0 – Email',           actionType: 'email',      casesAtStage: 67, activeAtStage: 32, closedAtStage: 18, promisedAtStage: 12, escalatedAtStage: 0, onHoldAtStage: 5,  avgDaysAtStage: 2 },
          { label: 'Day 5 – Phone Call',      actionType: 'phone_call', casesAtStage: 37, activeAtStage: 14, closedAtStage: 10, promisedAtStage: 8,  escalatedAtStage: 1, onHoldAtStage: 4,  avgDaysAtStage: 4 },
          { label: 'Day 10 – Email Follow-up', actionType: 'email',     casesAtStage: 19, activeAtStage: 3,  closedAtStage: 6,  promisedAtStage: 5,  escalatedAtStage: 2, onHoldAtStage: 3,  avgDaysAtStage: 3 },
        ],

      ),
      auditTrail: [
        {
          id: 'audit-1',
          date: subDays(new Date(), 1),
          event: 'run_completed',
          user: {
            name: 'System',
            id: 'system',
          },
          details: 'Sent 67 proactive check-in emails',
        },
        {
          id: 'audit-2',
          date: subDays(new Date(), 10),
          event: 'configuration_changed',
          user: {
            name: 'Sarah Johnson',
            id: 'user-1',
          },
          details: 'Adjusted pattern detection sensitivity',
        },
        {
          id: 'audit-3',
          date: subDays(new Date(), 20),
          event: 'created',
          user: {
            name: 'Sarah Johnson',
            id: 'user-1',
          },
        },
      ],
    },
    'strategy-6': {
      id: 'diag-6',
      strategyId: 'strategy-6',
      status: 'Active',
      createdBy: {
        name: 'Mike Chen',
        id: 'user-2',
      },
      createdAt: subDays(new Date(), 25),
      lastRun: subDays(new Date(), 2),
      nextScheduledRun: addDays(new Date(), 1),
      activeAccounts: 15,
      emailsSent14Days: 30,
      expectedEmails: 30,
      successRate: 72,
      impactAnalysis: {
        atRiskAccounts: {
          count: 15,
          description: 'Accounts with broken payment promises requiring follow-up',
        },
        totalExposure: {
          amount: 198000,
          currency: 'GBP',
          averagePerAccount: 13200,
          severity: 'medium',
        },
      },
      stageFunnel: buildFunnel(
        15,
        [
          { label: 'Day 1 – Phone Call',  actionType: 'phone_call', casesAtStage: 15, activeAtStage: 9, closedAtStage: 2, promisedAtStage: 2, escalatedAtStage: 0, onHoldAtStage: 2, avgDaysAtStage: 2 },
          { label: 'Day 3 – Email',       actionType: 'email',      casesAtStage: 11, activeAtStage: 4, closedAtStage: 2, promisedAtStage: 2, escalatedAtStage: 1, onHoldAtStage: 2, avgDaysAtStage: 3 },
          { label: 'Day 10 – Escalation', actionType: 'escalation', casesAtStage: 7,  activeAtStage: 0, closedAtStage: 2, promisedAtStage: 1, escalatedAtStage: 3, onHoldAtStage: 1, avgDaysAtStage: 7 },
        ],

      ),
      auditTrail: [
        {
          id: 'audit-1',
          date: subDays(new Date(), 2),
          event: 'run_completed',
          user: {
            name: 'System',
            id: 'system',
          },
          details: 'Sent 15 follow-up emails for broken promises',
        },
        {
          id: 'audit-2',
          date: subDays(new Date(), 12),
          event: 'configuration_changed',
          user: {
            name: 'Mike Chen',
            id: 'user-2',
          },
          details: 'Updated escalation timeline',
        },
        {
          id: 'audit-3',
          date: subDays(new Date(), 25),
          event: 'created',
          user: {
            name: 'Mike Chen',
            id: 'user-2',
          },
        },
      ],
    },
    'strategy-7': {
      id: 'diag-7',
      strategyId: 'strategy-7',
      status: 'Inactive',
      createdBy: {
        name: 'Mike Chen',
        id: 'user-2',
      },
      createdAt: subDays(new Date(), 50),
      lastRun: subDays(new Date(), 15),
      nextScheduledRun: undefined,
      activeAccounts: 0,
      emailsSent14Days: 0,
      expectedEmails: 45,
      successRate: 82,
      impactAnalysis: {
        notReceivingEmails: {
          count: 45,
          reason: 'Strategy has been inactive since 15 Nov 2024',
          since: subDays(new Date(), 15),
        },
        atRiskAccounts: {
          count: 12,
          description: 'Accounts moved to 90+ days overdue without collection follow-up',
        },
        totalExposure: {
          amount: 289000,
          currency: 'GBP',
          averagePerAccount: 6422,
          severity: 'high',
        },
      },
      stageFunnel: buildFunnel(
        54,
        [
          { label: 'Day 0 – Email',      actionType: 'email',      casesAtStage: 54, activeAtStage: 28, closedAtStage: 12, promisedAtStage: 8, escalatedAtStage: 0, onHoldAtStage: 6, avgDaysAtStage: 2 },
          { label: 'Day 3 – Phone Call', actionType: 'phone_call', casesAtStage: 34, activeAtStage: 9,  closedAtStage: 8,  promisedAtStage: 6, escalatedAtStage: 6, onHoldAtStage: 5, avgDaysAtStage: 4 },
        ],

      ),
      auditTrail: [
        {
          id: 'audit-1',
          date: subDays(new Date(), 15),
          event: 'deactivated',
          user: {
            name: 'Mike Chen',
            id: 'user-2',
          },
          reason: 'Testing email template changes',
        },
        {
          id: 'audit-2',
          date: subDays(new Date(), 20),
          event: 'configuration_changed',
          user: {
            name: 'Mike Chen',
            id: 'user-2',
          },
          details: 'Changed email template',
        },
        {
          id: 'audit-3',
          date: subDays(new Date(), 30),
          event: 'run_completed',
          user: {
            name: 'System',
            id: 'system',
          },
          details: 'Sent 44 emails to post-dispute accounts',
        },
        {
          id: 'audit-4',
          date: subDays(new Date(), 50),
          event: 'created',
          user: {
            name: 'Mike Chen',
            id: 'user-2',
          },
        },
      ],
    },
  };
}
